import React, { useEffect, useCallback, useState, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useNavigate } from 'react-router-dom';
import './HomePage.css';
import ImageSequence from './ImageSequence';
import ThreeDFeature from './ThreeDFeature';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const HomePage = () => {
    const navigate = useNavigate();

    // --- Contact form state ---
    const [contactMessage, setContactMessage] = useState('');
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [senderEmail, setSenderEmail] = useState('');
    const [contactStatus, setContactStatus] = useState('idle'); // idle | sending | success | error
    const [contactError, setContactError] = useState('');

    // --- Mobile menu state ---
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // --- Mobile detection (read once on mount) ---
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    // --- TARGET CALCULATIONS (conservées pour la navigation par menu) ---
    const getTargets = useCallback(() => {
        const vh = window.innerHeight;
        const t1 = vh * 5.0;
        const t2 = vh * 8.5;
        const t3 = vh * 11.0;
        const t4 = vh * 13.5;
        const t5 = vh * 16.0;
        const t6 = vh * 18.5;
        const t7 = vh * 20.0;
        return [0, t1, t2, t3, t4, t5, t6, t7];
    }, []);

    useEffect(() => {
        const mobile = window.innerWidth <= 768;
        // --- TIMELINES ---

        // 1. Hero (commun desktop + mobile)
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: ".hero-sequence-wrapper",
                start: "top top",
                end: "bottom bottom",
                scrub: true
            }
        });

        tl.to(".layer-back", { y: -150, opacity: 0, duration: 2 })
            .to(".layer-front", { y: -150, opacity: 0, duration: 2 }, "<")
            .fromTo(".text-sec-left h3", { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1 })
            .fromTo(".text-sec-right h3", { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1 }, "<+=0.2")
            .fromTo(".text-sec-left p", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1 }, ">-0.5")
            .fromTo(".text-sec-right p", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1 }, "<");

        if (!mobile) {
            // --- DESKTOP : bouton pill qui grossit dans la grille ---
            gsap.set(".bento-band-base", {
                width: "240px", height: "56px", borderRadius: "12px",
                justifySelf: "center", alignSelf: "center", padding: 0, cursor: "pointer"
            });
            tl.fromTo(".bento-band-base", { y: 150, opacity: 0 }, { y: 0, opacity: 1, duration: 1 });

            // 2. Bento grid expand (desktop seulement)
            const tlFeatures = gsap.timeline({
                scrollTrigger: {
                    trigger: ".bento-scroll-track",
                    start: "top top",
                    end: "bottom top",
                    scrub: true
                }
            });
            tlFeatures.to(".bento-cta-text", { opacity: 0, duration: 0.3 }, 0)
                .to(".bento-band-base", { width: "100%", height: "100%", borderRadius: "20px", duration: 1.8, ease: "power2.inOut", cursor: "default" }, 0)
                .to(".bento-content-inner", { opacity: 1, duration: 0.5 }, 2.0)
                .fromTo(".palette-header .line-1", { x: -50, opacity: 0 }, { x: 0, opacity: 1, duration: 0.8 }, 2.0)
                .fromTo(".palette-header .line-2", { x: -50, opacity: 0 }, { x: 0, opacity: 1, duration: 0.8 }, 2.2)
                .fromTo(".palette-header .line-3", { x: -50, opacity: 0 }, { x: 0, opacity: 1, duration: 0.8 }, 2.4)
                .fromTo(".bento-band-01", { xPercent: 100, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 1.2 }, 1.5)
                .fromTo(".bento-band-03", { yPercent: 100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 1.2 }, 1.7)
                .fromTo(".bento-band-02", { xPercent: -100, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 1.2 }, 1.9)
                .fromTo(".bento-band-00", { yPercent: -100, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 1.2 }, 2.1);

            // 3. Expansion slides (desktop seulement)
            const tlExpand = gsap.timeline({
                scrollTrigger: {
                    trigger: ".bento-expand-track",
                    start: "top top",
                    end: "bottom top",
                    scrub: true
                }
            });
            tlExpand.addLabel("entry", 0)
                .to([".bento-features-container", ".secondary-texts-container"], { yPercent: -150, opacity: 0, duration: 1.5, ease: "power2.inOut" }, 0)
                .to(".hero-media-layer", { yPercent: -150, opacity: 0, duration: 1.5 }, 0)
                .fromTo(".detailed-view-container", { yPercent: 100 }, { yPercent: 0, duration: 1.5, ease: "power2.inOut" }, 0)
                .set(".slide-00", { opacity: 1 }, 0)
                .fromTo(".floating-card-00", { xPercent: 120, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 0.8 }, 1.5)
                .set(".floating-card-00", { xPercent: 0, opacity: 1 }, 2.5)
                .to(".slide-00", { opacity: 0, duration: 1.2, ease: "power1.inOut" }, 3.5)
                .to(".floating-card-00", { xPercent: 120, opacity: 0, duration: 0.8 }, 3.8)
                .fromTo(".slide-01", { opacity: 0 }, { opacity: 1, duration: 1.2, ease: "power1.inOut" }, 3.5)
                .fromTo(".floating-card-01", { xPercent: -120, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 0.8 }, 4.2)
                .set(".floating-card-01", { xPercent: 0, opacity: 1 }, 5.0)
                .to(".slide-01", { opacity: 0, duration: 1.2, ease: "power1.inOut" }, 6.0)
                .to(".floating-card-01", { xPercent: -120, opacity: 0, duration: 0.8 }, 6.3)
                .fromTo(".slide-02", { opacity: 0 }, { opacity: 1, duration: 1.2, ease: "power1.inOut" }, 6.0)
                .fromTo(".floating-card-02", { xPercent: 120, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 0.8 }, 6.7)
                .set(".floating-card-02", { xPercent: 0, opacity: 1 }, 7.5)
                .to(".slide-02", { opacity: 0, duration: 1.2, ease: "power1.inOut" }, 8.5)
                .to(".floating-card-02", { xPercent: 120, opacity: 0, duration: 0.8 }, 8.8)
                .fromTo(".slide-03", { opacity: 0 }, { opacity: 1, duration: 1.2, ease: "power1.inOut" }, 8.5)
                .fromTo(".floating-card-03", { xPercent: -120, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 0.8 }, 9.2)
                .set(".floating-card-03", { xPercent: 0, opacity: 1 }, 10.0)
                .set(".slide-03", { opacity: 1 }, 10.0);

        } else {
            // --- MOBILE ---
            // ὾️ L'image s'estompe quand les textes Rapidité/Précision apparaissent
            tl.to(".hero-media-layer", { opacity: 0, duration: 1.5 }, 1.5);

            // � Bouton CTA pill fixe — cible l'élément dédié hors du bento-features-container caché
            gsap.set(".mobile-cta-pill", { opacity: 0, y: 20 });
            tl.fromTo(".mobile-cta-pill", { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 1 }, "<-0.5")
              .to(".mobile-cta-pill", { opacity: 1, duration: 2 })       // maintenu visible
              .to(".mobile-cta-pill", { opacity: 0, y: -20, duration: 0.5 }); // fade out final

            // Reveal séquentiel des cartes mobile au scroll
            const mobileCards = document.querySelectorAll(".mobile-bento-card");
            mobileCards.forEach((card) => {
                gsap.fromTo(card,
                    { opacity: 0, y: 30 },
                    {
                        scrollTrigger: {
                            trigger: card,
                            start: "top 88%",
                            toggleActions: "play none none none"
                        },
                        opacity: 1,
                        y: 0,
                        duration: 0.55,
                        ease: "power2.out"
                    }
                );
            });
        }

        return () => {
            ScrollTrigger.getAll().forEach(st => st.kill());
        };
    }, [getTargets]);

    // --- Contact handlers ---
    const handleContactSendClick = () => {
        if (!contactMessage.trim()) return;
        setContactStatus('idle');
        setContactError('');
        setSenderEmail('');
        setShowEmailModal(true);
    };

    const handleModalSubmit = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(senderEmail.trim())) {
            setContactError('Veuillez entrer une adresse email valide.');
            return;
        }

        setContactStatus('sending');
        setContactError('');

        try {
            const API_URL = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${API_URL}/api/contact/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: senderEmail.trim(),
                    message: contactMessage.trim(),
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setContactStatus('success');
                setContactMessage('');
                setTimeout(() => {
                    setShowEmailModal(false);
                    setContactStatus('idle');
                }, 2500);
            } else {
                setContactStatus('error');
                setContactError(data.error || "Une erreur est survenue. Réessayez.");
            }
        } catch (err) {
            setContactStatus('error');
            setContactError("Impossible de joindre le serveur. Vérifiez votre connexion.");
        }
    };

    const handleModalClose = () => {
        if (contactStatus === 'sending') return;
        setShowEmailModal(false);
        setContactStatus('idle');
        setContactError('');
    };

    return (
        <div className="home-container">
            {/* Navigation */}
            <nav className="home-nav">
                <div className="nav-left">
                    <div className="nav-logo">ARCHIVIZAI</div>
                    <ul className="nav-menu">
                        <li onClick={() => {
                            const targets = getTargets();
                            gsap.to(window, { 
                                scrollTo: targets[2], 
                                duration: 1.2, 
                                ease: "power2.inOut"
                            });
                        }}>Fonctionnalités</li>
                        <li onClick={() => {
                            const targets = getTargets();
                            gsap.to(window, { 
                                scrollTo: targets[7], 
                                duration: 1.5, 
                                ease: "power2.inOut"
                            });
                        }}>Tarifs</li>
                        <li style={{ cursor: 'pointer' }} onClick={() => navigate('/a-propos')}>À propos</li>
                        <li onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>Contactez-nous</li>
                    </ul>
                </div>
                {/* Hamburger — direct nav child, pushed to far right on mobile */}
                <button
                    className={`hamburger-btn${menuOpen ? ' is-open' : ''}`}
                    onClick={() => setMenuOpen(o => !o)}
                    aria-label="Menu"
                >
                    <span /><span /><span />
                </button>
                <div className="nav-links">
                    <button className="nav-btn login" onClick={() => navigate('/connexion')}>Connexion</button>
                    <button className="nav-btn subscribe" onClick={() => navigate('/inscription')}>S'abonner</button>
                </div>
            </nav>

            {/* Mobile slide-down menu */}
            <div ref={menuRef} className={`mobile-nav-drawer${menuOpen ? ' is-open' : ''}`}>
                <ul className="mobile-nav-list">
                    <li onClick={() => {
                        setMenuOpen(false);
                        const targets = getTargets();
                        gsap.to(window, { scrollTo: targets[2], duration: 1.2, ease: "power2.inOut" });
                    }}>Fonctionnalités</li>
                    <li onClick={() => {
                        setMenuOpen(false);
                        const targets = getTargets();
                        gsap.to(window, { scrollTo: targets[7], duration: 1.5, ease: "power2.inOut" });
                    }}>Tarifs</li>
                    <li onClick={() => { setMenuOpen(false); navigate('/a-propos'); }}>À propos</li>
                    <li onClick={() => { setMenuOpen(false); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}>Contactez-nous</li>
                    <li className="mobile-nav-cta" onClick={() => { setMenuOpen(false); navigate('/connexion'); }}>Connexion</li>
                    <li className="mobile-nav-cta primary" onClick={() => { setMenuOpen(false); navigate('/inscription'); }}>S'abonner</li>
                </ul>
            </div>

            {/* Sticky Wrapper for Hero + Features + Background */}
            <div className="scroll-container" style={{ position: 'relative', height: isMobile ? '500vh' : '2000vh' }}>

                <div className="sticky-content" style={{ zIndex: 10, position: 'sticky', top: 0, height: '100vh', width: '100%', overflow: 'hidden' }}>

                    {/* Layer 0: Back Text (Left, Behind) */}
                    <div className="hero-text-layer layer-back">
                        <h1 className="hero-title-large">Donnez</h1>
                        <p className="hero-title-large"><span className="highlight-grey">vie</span> à vos</p>
                    </div>

                    {/* Layer 1: Image Sequence (Center) */}
                    <div className="hero-media-layer layer-middle">
                        <ImageSequence />
                    </div>

                    {/* Layer 2: Front Text (Right, In Front) */}
                    <div className="hero-text-layer layer-front">
                        <h1 className="hero-title-large">Rendus</h1>
                    </div>

                    {/* Secondary Texts (Appear later) */}
                    <div className="secondary-texts-container">
                        <div className="text-sec-item text-sec-left">
                            <h3>Rapidité</h3>
                            <p>Visualisez vos projets instantanément grâce à notre moteur de rendu temps réel.</p>
                        </div>
                        <div className="text-sec-item text-sec-right">
                            <h3>Précision</h3>
                            <p>Un niveau de détail exceptionnel pour des présentations architecturales sans compromis.</p>
                        </div>
                    </div>



                    {/* New Bento Features Overlay (Superimposed on Hero) */}
                    <div id="palette-view-anchor" className="bento-features-container">
                        <div className="bento-card bento-band-base" onClick={(e) => {
                            if (e.currentTarget.offsetWidth < 300) navigate('/inscription');
                        }} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span className="bento-cta-text" style={{ pointerEvents: 'none', textTransform: 'uppercase', fontWeight: 600, fontSize: '1.1rem', color: '#111', position: 'absolute' }}>Commencer maintenant</span>

                            <div className="bento-content bento-content-inner" style={{ opacity: 0, width: '100%', padding: '4rem' }}>
                                <h2 className="palette-header" style={{ opacity: 1 }}>
                                    <span className="line-1"><span className="highlight-palette">UNE PALETTE</span> de</span>
                                    <span className="line-2"><span className="highlight-func">FONCTIONNALITÉS</span></span>
                                    <span className="line-1">pour GENERER des</span>
                                    <span className="line-3"><span className="highlight-photo">IMAGES</span></span>
                                    <span className="line-2"><span className="highlight-photo">PHOTOREALISTES</span></span>
                                </h2>
                            </div>
                        </div>

                        <div className="bento-card bento-band-00">
                            <span className="bento-num">00</span>
                            <h3 className="bento-title">IMPORT IMAGES</h3>
                            <p className="bento-desc">Rendu photo instantané</p>
                            <p className="bento-body">Transformez vos modèles bruts en visuels photoréalistes. Notre technologie applique des textures intelligemment sur les zones clés de l'image.</p>
                        </div>

                        <div className="bento-card bento-band-01">
                            <span className="bento-num">01</span>
                            <h3 className="bento-title">CREATION DESSIN</h3>
                            <p className="bento-desc">Esquisses vers réalité</p>
                            <p className="bento-body">Libérez votre créativité. Esquissez vos volumes et laissez l'IA transformer vos idées en visuels exceptionnels.</p>
                        </div>

                        <div className="bento-card bento-band-02">
                            <span className="bento-num">02</span>
                            <h3 className="bento-title">TEXTURE & FOND</h3>
                            <p className="bento-desc">Ambiance et matériaux</p>
                            <p className="bento-body">Sublimez vos créations. Intégrez vos images de fond, importez jusqu'à 8 textures et laissez la composition opérer instantanément.</p>
                        </div>

                        <div className="bento-card bento-band-03">
                            <span className="bento-num">03</span>
                            <h3 className="bento-title">IMPORT VOLUME 3D</h3>
                            <p className="bento-desc">Maquettes sublimées</p>
                            <p className="bento-body">Donnez vie à vos volumes 3D bruts. Visualisez-les sous tous les angles et magnifiez leurs rendus de manière déconcertante.</p>
                        </div>
                    </div>

                    {/* Separate container for detailed view that comes from bottom */}
                    <div className="detailed-view-container">
                        <div className="building-slideshow">
                            <img src="/assets/building/building_volume.png" alt="Building Volume" className="slideshow-image slide-00" />
                            <img src="/assets/building/building_draw.png" alt="Building Draw" className="slideshow-image slide-01" />
                            <img src="/assets/building/building_photo.png" alt="Building Photo" className="slideshow-image slide-02" />
                            
                            {/* Interactive 3D Volume for Card 03 */}
                            <div className="slideshow-image slide-03">
                                <ThreeDFeature />
                            </div>

                            <div className="slideshow-cards">
                                <div className="bento-card floating-card floating-card-00">
                                    <span className="bento-num">00</span>
                                    <h3 className="bento-title">IMPORT IMAGES</h3>
                                    <p className="bento-desc">Rendu photo instantané</p>
                                    <p className="bento-body">Importez vos modèles 2D brutes ou vos maquettes et rédigez vos prompts pour ajouter de la végétation ou des personnages. Transformez vos rendus simples en visuels photoréalistes : notre technologie applique intelligemment les textures sur les zones clés de l'image.</p>
                                </div>

                                <div className="bento-card floating-card floating-card-01">
                                    <span className="bento-num">01</span>
                                    <h3 className="bento-title">CREATION DESSIN</h3>
                                    <p className="bento-desc">Esquisses vers réalité</p>
                                    <p className="bento-body">Libérez votre créativité. Esquissez vos volumes directement dans l'application grâce à une palette d'outils avec assistance au tracé, et laissez l'IA transformer vos idées en visuels exceptionnels.</p>
                                </div>

                                <div className="bento-card floating-card floating-card-02">
                                    <span className="bento-num">02</span>
                                    <h3 className="bento-title">TEXTURE & FOND</h3>
                                    <p className="bento-desc">Ambiance et matériaux</p>
                                    <p className="bento-body">Sublimez vos créations. Intégrez vos images de fond, importez jusqu'à 8 textures et laissez la composition opérer instantanément.</p>
                                </div>

                                <div className="bento-card floating-card floating-card-03">
                                    <span className="bento-num">03</span>
                                    <h3 className="bento-title">IMPORT VOLUME 3D</h3>
                                    <p className="bento-desc">Maquettes sublimées</p>
                                    <p className="bento-body">Donnez vie à vos volumes 3D bruts. Visualisez-les sous tous les angles et magnifiez leurs rendus de manière déconcertante.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Triggers for GSAP Timelines placed absolutely to not affect layout but map scroll positions */}
                <div className="hero-sequence-wrapper" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '500vh', pointerEvents: 'none' }}></div>
                <div className="bento-scroll-track" style={{ position: 'absolute', top: '500vh', left: 0, width: '100%', height: '350vh', pointerEvents: 'none' }}></div>
                <div className="bento-expand-track" style={{ position: 'absolute', top: '850vh', left: 0, width: '100%', height: '1000vh', pointerEvents: 'none' }}></div>
            </div>

            {/* Bouton CTA mobile fixe — hors du bento-features-container (caché sur mobile) */}
            <div className="mobile-cta-pill" onClick={() => navigate('/inscription')}>
                <span>Commencer maintenant</span>
            </div>

            {/* ===== SECTION MOBILE BENTO (hors scroll-container, avant les prix) ===== */}
            <div className="mobile-bento-section">

                {/* 2. Carte palette */}
                <div className="mobile-bento-card bento-card mobile-bento-palette">
                    <h2 className="palette-header">
                        <span className="line-1"><span className="highlight-palette">UNE PALETTE</span> de</span>
                        <span className="line-2"><span className="highlight-func">FONCTIONNALITÉS</span></span>
                        <span className="line-1">pour GENERER des</span>
                        <span className="line-3"><span className="highlight-photo">IMAGES</span></span>
                        <span className="line-2"><span className="highlight-photo">PHOTORÉALISTES</span></span>
                    </h2>
                </div>

                {/* 3. Feature 00 — image building_volume + carte */}
                <div className="mobile-feature-block mobile-bento-card">
                    <div className="mobile-feature-img">
                        <img src="/assets/building/building_volume.png" alt="Import Images" />
                    </div>
                    <div className="bento-card mobile-feature-card">
                        <span className="bento-num">00</span>
                        <h3 className="bento-title">IMPORT IMAGES</h3>
                        <p className="bento-desc">Rendu photo instantané</p>
                        <p className="bento-body">Transformez vos modèles bruts en visuels photoréalistes. Notre technologie applique des textures intelligemment sur les zones clés de l'image.</p>
                    </div>
                </div>

                {/* 4. Feature 01 — image building_draw + carte */}
                <div className="mobile-feature-block mobile-bento-card">
                    <div className="mobile-feature-img">
                        <img src="/assets/building/building_draw.png" alt="Création Dessin" />
                    </div>
                    <div className="bento-card mobile-feature-card">
                        <span className="bento-num">01</span>
                        <h3 className="bento-title">CREATION DESSIN</h3>
                        <p className="bento-desc">Esquisses vers réalité</p>
                        <p className="bento-body">Libérez votre créativité. Esquissez vos volumes et laissez l'IA transformer vos idées en visuels exceptionnels.</p>
                    </div>
                </div>

                {/* 5. Feature 02 — image building_photo + carte */}
                <div className="mobile-feature-block mobile-bento-card">
                    <div className="mobile-feature-img">
                        <img src="/assets/building/building_photo.png" alt="Texture &amp; Fond" />
                    </div>
                    <div className="bento-card mobile-feature-card">
                        <span className="bento-num">02</span>
                        <h3 className="bento-title">TEXTURE &amp; FOND</h3>
                        <p className="bento-desc">Ambiance et matériaux</p>
                        <p className="bento-body">Sublimez vos créations. Intégrez vos images de fond, importez jusqu'à 8 textures et laissez la composition opérer instantanément.</p>
                    </div>
                </div>

                {/* 6. Feature 03 — carte seule (3D trop complexe sur mobile) */}
                <div className="mobile-bento-card bento-card">
                    <span className="bento-num">03</span>
                    <h3 className="bento-title">IMPORT VOLUME 3D</h3>
                    <p className="bento-desc">Maquettes sublimées</p>
                    <p className="bento-body">Donnez vie à vos volumes 3D bruts. Visualisez-les sous tous les angles et magnifiez leurs rendus de manière déconcertante.</p>
                </div>

            </div>

            {/* Pricing Section */}
            <section className="pricing-section">
                <h2 className="section-title">Nos Offres</h2>
                <div className="pricing-grid">
                    <div className="pricing-card">
                        <h3>Essai Gratuit</h3>
                        <div className="price">0€<span></span></div>
                        <ul className="features">
                            <li>5 Renders</li>
                            <li>Résolution Standard</li>
                            <li>Usage Personnel</li>
                        </ul>
                        <button className="price-btn" onClick={() => navigate('/inscription')}>Essayer</button>
                    </div>

                    <div className="pricing-card">
                        <h3>Standard</h3>
                        <div className="price">9.90€<span>/mois</span></div>
                        <ul className="features">
                            <li>10 Renders / mois</li>
                            <li>Résolution Standard (1080p)</li>
                            <li>Support Email</li>
                        </ul>
                        <button className="price-btn" onClick={() => navigate('/inscription')}>Choisir</button>
                    </div>

                    <div className="pricing-card popular">
                        <div className="badge">Populaire</div>
                        <h3>Pro</h3>
                        <div className="price">19.90€<span>/mois</span></div>
                        <ul className="features">
                            <li>20 Renders / mois</li>
                            <li>Haute Résolution (4K)</li>
                            <li>Mode &quot;Capture de Vue&quot; 3D</li>
                            <li>Support Prioritaire</li>
                        </ul>
                        <button className="price-btn primary" onClick={() => navigate('/inscription')}>Choisir</button>
                    </div>

                    <div className="pricing-card">
                        <h3>Entreprise</h3>
                        <div className="price">Sur devis</div>
                        <ul className="features">
                            <li>Renders Illimités</li>
                            <li>API Access</li>
                            <li>Chef de projet dédié</li>
                        </ul>
                        <button className="price-btn" onClick={() => navigate('/abonnement')}>Contacter</button>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="contact-section" id="contact">
                <h2 className="section-title">Contactez-nous</h2>
                <div className="contact-container">
                    <p className="contact-desc">Une question ? Un projet ? Écrivez-nous.</p>
                    <textarea
                        id="contact-message"
                        className="contact-textarea"
                        placeholder="Votre message..."
                        value={contactMessage}
                        onChange={(e) => setContactMessage(e.target.value)}
                    />
                    <button
                        className="contact-btn"
                        onClick={handleContactSendClick}
                        disabled={!contactMessage.trim()}
                    >
                        Envoyer
                    </button>
                </div>
            </section>

            <footer className="home-footer">
                <p>&copy; 2025 ARCHIVIZAI. All rights reserved.</p>
            </footer>

            {/* Email Modal */}
            {showEmailModal && (
                <div className="contact-modal-overlay" onClick={handleModalClose}>
                    <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
                        {contactStatus === 'success' ? (
                            <div className="contact-modal-success">
                                <div className="contact-success-icon">✓</div>
                                <h3>Message envoyé !</h3>
                                <p>Nous vous répondrons dans les plus brefs délais.</p>
                            </div>
                        ) : (
                            <>
                                <button className="contact-modal-close" onClick={handleModalClose} disabled={contactStatus === 'sending'}>✕</button>
                                <div className="contact-modal-header">
                                    <h3>Votre adresse email</h3>
                                    <p>Pour que nous puissions vous répondre, entrez votre adresse email.</p>
                                </div>
                                <input
                                    id="contact-sender-email"
                                    type="email"
                                    className="contact-modal-input"
                                    placeholder="votre@email.com"
                                    value={senderEmail}
                                    onChange={(e) => {
                                        setSenderEmail(e.target.value);
                                        setContactError('');
                                    }}
                                    onKeyDown={(e) => e.key === 'Enter' && handleModalSubmit()}
                                    disabled={contactStatus === 'sending'}
                                    autoFocus
                                />
                                {contactError && (
                                    <p className="contact-modal-error">{contactError}</p>
                                )}
                                <button
                                    className="contact-modal-submit"
                                    onClick={handleModalSubmit}
                                    disabled={contactStatus === 'sending' || !senderEmail.trim()}
                                >
                                    {contactStatus === 'sending' ? (
                                        <span className="contact-modal-spinner" />
                                    ) : (
                                        'Envoyer le message'
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomePage;
